import React, { forwardRef, useState, useEffect } from "react";
import { Icons, Stack, Box, IconButton, AutocompleteField } from "@curves/ui-base";
import BarcodeScannerDialog from "@icoach/pos/dialog/BarcodeScannerDialog";
import usePosApi from "@apis/usePosApi";
import { isEmpty, isFunction } from "@util/utils";

/**
 * 會員設定組件
 * 支援手動選擇和條碼掃描兩種方式
 * @param {Object} props - 組件屬性
 * @param {Object} props.memberData - 會員資料
 * @param {Function} props.onChange - 會員選擇變更回調函數
 * @param {boolean} [props.enableScan=false] - 是否啟用條碼掃描功能
 * @param {string} [props.className] - 自定義CSS類名
 * @param {Array} [props.peopleOption=[]] - 會員選項列表
 * @param {Function} [props.onDialogStateChange] - 對話框狀態變化回調 (dialogName, isOpen)
 * @param {Object} [props.props] - 其他傳遞給根元素的屬性
 */
const MEMBER_SCAN_DIALOG = "MemberScanDialog";

const MemberSetting = forwardRef(({ className, memberData, enableScan = false, onChange, peopleOption = [], onDialogStateChange, ...otherProps }, ref) => {
	const { getPosMemberInfoApi } = usePosApi();
	const [showScanDialog, setShowScanDialog] = useState(false);

	// 處理對話框打開
	const handleOpenDialog = () => {
		setShowScanDialog(true);
		if (enableScan && isFunction(onDialogStateChange)) {
			onDialogStateChange(MEMBER_SCAN_DIALOG, true);
		}
	};

	// 處理對話框關閉
	const handleCloseDialog = () => {
		setShowScanDialog(false);
		if (enableScan && isFunction(onDialogStateChange)) {
			onDialogStateChange(MEMBER_SCAN_DIALOG, false);
		}
	};

	// 同步狀態：通知父組件對話框狀態變化
	useEffect(() => {
		if (enableScan && isFunction(onDialogStateChange)) {
			onDialogStateChange(MEMBER_SCAN_DIALOG, showScanDialog);
		}
	}, [showScanDialog, enableScan, onDialogStateChange]);

	// 處理掃描成功 - 邏輯集中，易於理解
	const handleScanSuccess = async memberCode => {
		if (!memberCode || !memberCode.trim()) return false;

		try {
			const resp = await getPosMemberInfoApi(memberCode.trim());

			if (resp) {
				const memberData = resp;
				// 成功：更新會員資料
				const member = {
					id: memberData.id,
					no: memberData.no,
					cardNo: memberData.cardNo,
					name: memberData.name,
					nickName: memberData.nickName,
					mobile: memberData.mobile,
					clubText: memberData.clubText,
				};

				isFunction(onChange) && onChange(member);

				return true;
			} else {
				return false;
			}
		} catch (err) {
			console.error("掃描會員條碼失敗:", err);
			return false; // 返回失敗，對話框保持開啟
		}
	};

	// 處理 AutocompleteField 的選擇
	const handleAutocompleteChange = newValue => {
		isFunction(onChange) && onChange(newValue);
	};

	return (
		<Box className={className} {...otherProps}>
			{isEmpty(memberData) ? (
				<Stack alignItems="center">
					<AutocompleteField
						ref={ref}
						options={peopleOption}
						inputProps={{
							placeholder: "輸入會員姓名或編號",
						}}
						primaryKey={"id"}
						optionKeys={["name", "nickName", "mobile", "no"]}
						onChange={handleAutocompleteChange}
						fullWidth
						required
					/>
					{enableScan && (
						<IconButton
							onClick={handleOpenDialog}
							style={{
								background: "none",
								border: "none",
								padding: "8px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Icons.CarrierCode />
						</IconButton>
					)}
				</Stack>
			) : (
				<Stack justifyContent="space-between" alignItems="center">
					<Box className="pos-member-display">
						<Box className="member-title">{`${memberData.no} ${memberData.name}`}</Box>
						<Box className="member-subtitle">{memberData.clubText}</Box>
					</Box>
					<IconButton
						onClick={() => onChange(null)} // 清除時傳入 null
						style={{
							background: "none",
							border: "none",
							padding: "8px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Icons.Close />
					</IconButton>
				</Stack>
			)}

			{enableScan && <BarcodeScannerDialog open={showScanDialog} onClose={handleCloseDialog} onScanSuccess={handleScanSuccess} title="會員條碼掃描" />}
		</Box>
	);
});

export default MemberSetting;
